/**
 * components/import/statements/NewAccountInlineDialog.jsx — Phase 84.3 slice-1
 *
 * Minimal create-account dialog reused from /accounts. Lets the user add
 * an account without leaving the import flow. Accepts an optional
 * `defaults` prop with prefilled fields, used when the
 * StatementAccountDialog hands off after detecting a new account from
 * the parsed statement header. Extracted verbatim from pages/CSVImport.js
 * (was internal function `NewAccountInlineDialog`). Zero behavior change.
 *
 * H1 · Currency picker is REQUIRED — no silent fallback to profile
 * currency. When `defaults.currency` is supplied (e.g. Gemini-detected
 * from a PDF), it prefills but user can override.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/context/AuthContext';
import { CURRENCIES } from '@/lib/format';
import { resolveDefaultCurrency } from '@/lib/defaultCurrency';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

export default function NewAccountInlineDialog({ open, setOpen, onCreated, defaults }) {
  const { user, profile } = useAuth();
  const [form, setForm] = useState({ name: '', type: 'bank', last4: '', currency: '' });
  const [saving, setSaving] = useState(false);
  const reset = () => setForm({ name: '', type: 'bank', last4: '', currency: '' });

  // When the dialog opens with prefilled defaults (e.g. from the
  // statement-account reconciliation flow), seed the form. We do this on
  // `open` flipping true so reopening with new defaults always re-seeds.
  React.useEffect(() => {
    if (!open) return;
    if (defaults && typeof defaults === 'object') {
      setForm({
        name: defaults.name || '',
        type: defaults.type || 'bank',
        last4: defaults.last4 || '',
        // H1 · Preserve the detected-currency prefill path. When callers
        // supply `defaults.currency` (e.g. StatementAccountDialog with a
        // Gemini-detected code), we honour it. Otherwise fall back to what the
        // profile or the person's country implies — and to blank when neither
        // is known, so the required-field guard in save() forces a real pick.
        currency: defaults.currency || resolveDefaultCurrency(profile) || '',
      });
    } else {
      reset();
    }
  }, [open]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!form.name.trim()) { toast.error('Give your account a name.'); return; }
    // H1 · Currency is required. No silent fallback to profile currency.
    if (!form.currency) {
      toast.error('Pick a currency for this account.');
      return;
    }
    const last4 = (form.last4 || '').trim();
    if (last4 && !/^\d{4}$/.test(last4)) {
      toast.error('Last 4 digits must be exactly 4 numbers (or leave blank).');
      return;
    }
    setSaving(true);
    try {
      const baseRow = {
        user_id: user.id,
        name: form.name.trim(),
        type: form.type,
        currency: form.currency,
      };
      // Phase 17, gracefully degrade if accounts.last4 column hasn't been added yet.
      const row = last4 ? { ...baseRow, last4 } : baseRow;
      let result = await supabase.from('accounts').insert(row).select('id').single();
      if (result.error && /column .*last4.* does not exist|PGRST204/i.test(`${result.error.message} ${result.error.code || ''}`)) {
        console.warn('accounts.last4 column missing, retrying without it. Apply schema_phase17.sql.');
        result = await supabase.from('accounts').insert(baseRow).select('id').single();
        if (!result.error) {
          toast.warning('Account saved without last 4 digits, apply the latest schema migration to enable that field.', { duration: 7000 });
        }
      }
      if (result.error) throw result.error;
      toast.success('Account added.');
      setOpen(false);
      reset();
      onCreated?.(result.data?.id || null);
    } catch (err) {
      console.error(err);
      const raw = err?.message || 'Could not save account.';
      const friendly = /disturbed or locked/i.test(raw)
        ? 'Database rejected the insert. The schema migration may still be pending, apply schema_phase17.sql in Supabase SQL Editor.'
        : raw;
      toast.error(friendly, { duration: 9000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogContent className="rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">New account</DialogTitle>
          <DialogDescription>Where does this statement come from?</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Name</div>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Chequing, Savings, Credit card…"
              className="rounded-xl"
              data-testid="inline-account-name"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Type</div>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="rounded-xl" data-testid="inline-account-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank account</SelectItem>
                <SelectItem value="credit_card">Credit card</SelectItem>
                <SelectItem value="wallet">Wallet</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="investment">Investment</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* H1 · Currency picker. Explicit selection required (no silent
              default to profile currency). Prefilled when the caller passed
              defaults.currency (e.g. from a PDF's detected statement
              currency). */}
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Currency</div>
            <Select
              value={form.currency || undefined}
              onValueChange={(v) => setForm({ ...form, currency: v })}
            >
              <SelectTrigger className="rounded-xl" data-testid="inline-account-currency">
                <SelectValue placeholder="Choose currency…" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              The currency this account&rsquo;s statements are in. Choose carefully &mdash; it can&rsquo;t be changed silently later.
            </p>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Last 4 digits (optional)</div>
            <Input
              inputMode="numeric"
              maxLength={4}
              value={form.last4}
              onChange={(e) => setForm({ ...form, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              placeholder="e.g. 3421"
              className="rounded-xl tabular-nums tracking-widest"
              data-testid="inline-account-last4"
            />
            <p className="text-[11px] text-muted-foreground">Just the last 4 , helps you tell two cards apart. Never the full number, CVV, or expiry.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl" data-testid="inline-account-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving&hellip;</> : 'Save & select'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
